import React, { useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import CustomTable from '../../../shared/components/table/custom-table';
import { FloatingButtonContainer, InnerButton } from '../../../shared/components/buttons/floating-button-container';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import useNotification from '../../../shared/hooks/useNotification';
import { useApi } from '../../../shared/hooks/useApi';
import { LoaderComponent } from '../../../shared/components/loaders/page-loader';
import _ from 'lodash';
import { ETCAppConfig } from '../../../shared/app-configs/etc-app-configs';
import { Utills } from '../../../shared/app-configs/utilit';
import { StyledButton } from '../../../shared/components/buttons/custom-button';
// import { mapJsonToPayload } from '../etc-models/IProduct-model';

const ProductDetails = () => {
  const { notify } = useNotification()
  const navigate = useNavigate();
  const { etcId, formtype } = useParams<string>()
  const [data, setData] = useState<any[]>([]);
  const { loading, executeGet } = useApi();
  // Refs for Form Steps
  const componentInfoRef = useRef<any>(null);
  const formRefs = [componentInfoRef];

  const fetchProductDetails = async () => {
    try {
      const response: any = await executeGet(ETCAppConfig.GET_PRODUCT_DETAILS, { etcId: etcId });
      Utills.cl("response", response)
      // const result = mapJsonToPayload(response.data.tcProductInformation, 100);
      // Utills.cl("result", result)
      setData(response.data.tcProductInformation);
    } catch (err) {
      console.error('Failed to fetch Product Details:', err);
    }
  }

  useEffect(() => {
    fetchProductDetails()
  }, []);

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'tcProductShipmentNo',
      header: 'Shipment No.',
      enableHiding: false,
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: any) => {
        const selectedValues = cell.getValue() || [];
        return (
          <div>
            {Array.isArray(selectedValues) && selectedValues.length > 0
              ? selectedValues.join(", ") // Convert array to comma-separated string
              : "-"}
          </div>
        );
      },
    },
    { accessorKey: 'tcProductNo', header: 'Product No.', enableHiding: false, minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductCategoryCode', header: 'Product Category', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductCategoryUserSpecificTerm', header: 'Product Category User Specific Term', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductDetailCode', header: 'Product Detail', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductDetailUserSpecificTerm', header: 'Product Detail User Specific Term', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductProductionDate', header: 'Production Date', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductNetShippingWeight', header: 'Net Shipping Weight (Kg)', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductSupplementaryWeight', header: 'Supplementary Weight (Kg)', minSize: 60, maxSize: 150 },
  
    {
      accessorKey: 'tcProductStandardInformation',
      header: 'Product Certified Weight for GRS (Kg)',
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: any) => {
        const standards = cell.getValue() || [];
        return standards.length
          ? standards.map((s: any) => s.tcProductCertifiedWeight).join(", ")
          : "-";
      },
    },
  
    {
      accessorKey: 'inputTcInformation',
      header: 'eTC Minimum Wastage Factor',
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: any) => {
        const inputTcInfo = cell.getValue() || [];
        if (Array.isArray(inputTcInfo) && inputTcInfo.length > 0) {
          const productInfo = inputTcInfo[0]?.inputTcProductInformation || [];
          return productInfo.length > 0 && productInfo[0].etcMinimumWastageFactor
            ? productInfo[0].etcMinimumWastageFactor
            : "-";
        }
        return "-";
      },
    },
  
    {
      accessorKey: 'tcProductStandardInformation',
      header: 'Standard Label Grade',
      minSize: 60,
      maxSize: 150,
      Cell: ({ cell }: any) => {
        const standards = cell.getValue() || [];
        return standards.length
          ? standards.map((s: any) => s.tcProductStandardLabelGrade).join(", ")
          : "-";
      },
    },
  
    { accessorKey: 'tcProductOrderNo', header: 'Order No.', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductArticleNo', header: 'Article No.', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductNumberOfUnits', header: 'No. of Units', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductUnitsMetric', header: 'Unit Metric', minSize: 60, maxSize: 150 },
    { accessorKey: 'tcProductInfoAssociatedTc', header: 'Associated eTC', minSize: 60, maxSize: 150 },
  ];
  
  const handleSave = async () => {
    navigate(`/etc/${formtype}/input-etc-components-details/${etcId}`)
  }
  const renderDetailPanel = ({ row }: { row: any }) => {
    const { original } = row;
  
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 5,
          gap: 4,
          width: '50%',
        }}
      >
        {/* eTC Product Details */}
        <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
          <Typography variant="body2" fontWeight="400" sx={{ mb: 1 }}>
            eTC Product Details
          </Typography>
          <CustomTable
            columns={[
              { accessorKey: 'ocsProcessor', header: 'Last Processor TE-Certified?', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductLastProcessorTeId', header: 'Last Processor TE-ID', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductLastProcessorName', header: 'Last Processor Name', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductLastProcessorLicenseNo', header: 'Last Processor License No.', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductLastProcessorCountry', header: 'Last Processor Country', minSize: 60, maxSize: 150 },
            ]}
            data={[original]}
            enableRowActions={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
          />
        </Box>
        <Divider />
  
        {/* Product Component Standard Information */}
        <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
          <Typography variant="body2" fontWeight="400" sx={{ mb: 1 }}>
            Product Component Standard Information
          </Typography>
          <CustomTable
            columns={[
              { accessorKey: 'tcProductInfoFiberLength', header: 'Fiber Length(mm)', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductInfoYarnCount', header: 'Yarn Count', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductInfoFabricWeightAndConstruct', header: 'Fabric Weight and Construct', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductInfoFabricWeightOfFinishedProduct', header: 'Fabric Weight of Finished Product', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductInfoTrimsExcluded', header: 'Trim Excluded', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductInfoAdditional', header: 'Additional Info', minSize: 60, maxSize: 150 },
            ]}
            data={[original]}
            enableRowActions={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
          />
        </Box>
        <Divider />
  
        {/* Raw Material Composition */}
        <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
          <Typography variant="body2" fontWeight="400" sx={{ mb: 1 }}>
            Raw Material Composition
          </Typography>
          <CustomTable
            columns={[
              { accessorKey: 'tcProductRawMaterialCode', header: 'Raw Material Code', minSize: 60, maxSize: 150 },
              { accessorKey: 'tcProductRawMaterialPercentage', header: 'Raw Material Percentage', minSize: 60, maxSize: 150 },
            ]}
            data={original.tcProductRawMaterialInformation || []}
            enableRowActions={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
          />
        </Box>
      </Box>
    );
  };
  
  return (
    <>
      {loading ? <LoaderComponent /> : <>
        <Box display="grid">
          <CustomTable
            columns={columns}
            data={data}
            enableTopToolbar={false}
            enableBottomToolbar={true}
            renderDetailPanel={renderDetailPanel}
          />
        </Box>
        <Box sx={{
          display: "flex", justifyContent: "flex-end", mt: 2
        }}>
          <StyledButton variant="contained" onClick={handleSave}>
            Next
          </StyledButton>
          <NavLink to="/tc-dashboard">
            <StyledButton variant="contained">
              Cancel
            </StyledButton>
          </NavLink>
        </Box>
      </>}
    </>
  );
};

export default ProductDetails;